import React, { useEffect, useRef, useState } from "react";
import { adminApi, formatErr } from "./api";
import { useEscapeKey } from "./hooks";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";

const empty = {
  sku: "", name: "", variant: "", mrp: 0, selling_price: 0,
  stock_quantity: 0, image_url: "", description: "",
  status: "active", low_stock_threshold: 100,
  location: "", discount_percent: 0, pack_size: "single",
};

const PACK_LABEL = { "single": "Single", "3-pack": "3-pack", "10-pack": "10-pack" };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEscapeKey(open, () => setOpen(false));
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await adminApi.get("/products");
    setItems(data.products);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p.sku); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        mrp: Number(form.mrp),
        selling_price: Number(form.selling_price),
        stock_quantity: Number(form.stock_quantity),
        low_stock_threshold: Number(form.low_stock_threshold),
        discount_percent: form.discount_percent === "" || form.discount_percent == null ? null : Number(form.discount_percent),
        location: form.location?.trim() || null,
        pack_size: form.pack_size || "single",
      };
      if (editing) await adminApi.put(`/products/${editing}`, payload);
      else await adminApi.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (sku) => {
    if (!confirm(`Delete product ${sku}?`)) return;
    try {
      await adminApi.delete(`/products/${sku}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const onCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await adminApi.post("/products/bulk-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-eyebrow text-gold">Catalog</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Products</h1>
        </div>
        <div className="flex gap-3">
          <input ref={fileRef} type="file" accept=".csv" onChange={onCsv} className="hidden" data-testid="csv-upload-input" />
          <button onClick={() => fileRef.current?.click()} className="btn-ghost !py-3" data-testid="bulk-csv-btn">
            <Upload className="w-3.5 h-3.5" /> Bulk CSV
          </button>
          <button onClick={openNew} className="btn-primary !py-3" data-testid="new-product-btn">
            <Plus className="w-3.5 h-3.5" /> New product
          </button>
        </div>
      </div>

      <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="products-table">
        <table className="w-full text-sm">
          <thead className="bg-ink-950">
            <tr className="text-left label-eyebrow text-mist">
              <th className="px-4 py-3">SKU</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Variant</th>
              <th className="px-4 py-3">Pack</th><th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">MRP</th><th className="px-4 py-3">Selling</th><th className="px-4 py-3">Disc%</th>
              <th className="px-4 py-3">Stock</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const computedDisc = p.discount_percent != null
                ? Number(p.discount_percent)
                : (p.mrp && p.selling_price ? Math.round(((p.mrp - p.selling_price) / p.mrp) * 100) : 0);
              return (
                <tr key={p.sku} className="border-t border-white/5 text-bone/90 font-light" data-testid={`product-row-${p.sku}`}>
                  <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                  <td className="px-4 py-3">{p.name}</td>
                  <td className="px-4 py-3 text-mist">{p.variant}</td>
                  <td className="px-4 py-3"><span className="label-eyebrow text-mist">{PACK_LABEL[p.pack_size] || "Single"}</span></td>
                  <td className="px-4 py-3 text-mist text-xs">{p.location || "—"}</td>
                  <td className="px-4 py-3 num-display">₹{p.mrp}</td>
                  <td className="px-4 py-3 text-gold num-display">₹{p.selling_price}</td>
                  <td className="px-4 py-3 num-display">{computedDisc > 0 ? `${computedDisc}%` : "—"}</td>
                  <td className={`px-4 py-3 num-display ${p.stock_quantity < p.low_stock_threshold ? "text-rose-300" : ""}`}>{p.stock_quantity}</td>
                  <td className="px-4 py-3"><span className={`label-eyebrow ${p.status === "active" ? "text-gold" : "text-mist"}`}>{p.status}</span></td>
                  <td className="px-4 py-3 flex gap-2 justify-end">
                    <button onClick={() => openEdit(p)} className="text-mist hover:text-gold p-1.5" data-testid={`edit-product-${p.sku}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(p.sku)} className="text-mist hover:text-rose-300 p-1.5" data-testid={`delete-product-${p.sku}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-ink-900 border border-white/10 w-full max-w-2xl p-8 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-2xl text-bone">{editing ? "Edit product" : "New product"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input required disabled={!!editing} value={form.sku} onChange={set("sku")} placeholder="SKU" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="product-sku-input" />
              <input required value={form.name} onChange={set("name")} placeholder="Name" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="product-name-input" />
              <input value={form.variant} onChange={set("variant")} placeholder="Variant" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <select value={form.status} onChange={set("status")} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold">
                <option value="active">Active</option><option value="draft">Draft</option><option value="archived">Archived</option>
              </select>
              <input type="number" step="0.01" value={form.mrp} onChange={set("mrp")} placeholder="MRP" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input type="number" step="0.01" value={form.selling_price} onChange={set("selling_price")} placeholder="Selling price" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input type="number" value={form.stock_quantity} onChange={set("stock_quantity")} placeholder="Stock qty" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <input type="number" value={form.low_stock_threshold} onChange={set("low_stock_threshold")} placeholder="Low-stock threshold" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <select value={form.pack_size} onChange={set("pack_size")} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="product-pack-size">
                <option value="single">Single unit</option>
                <option value="3-pack">3-pack</option>
                <option value="10-pack">10-pack</option>
              </select>
              <input type="number" step="0.01" min="0" max="100" value={form.discount_percent ?? ""} onChange={set("discount_percent")} placeholder="Discount %" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="product-discount-input" />
              <input value={form.location || ""} onChange={set("location")} placeholder="Location (e.g., Mumbai godown)" className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="product-location-input" />
              <input value={form.image_url || ""} onChange={set("image_url")} placeholder="Image URL or /products/x.jpg" className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <textarea value={form.description || ""} onChange={set("description")} placeholder="Description" rows={3} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold resize-none" />
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary !py-3" data-testid="save-product-btn">{busy ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </div>
      )}

      <p className="text-mist text-xs font-light">
        CSV columns: <code className="text-gold">sku, name, variant, mrp, selling_price, stock_quantity, image_url, description, status, low_stock_threshold, location, discount_percent, pack_size</code>
      </p>
    </div>
  );
}
